/*
 * Property editors for the free-design widgets (docs/hmi-panel-ausbau.md
 * §6), plus three sections every widget gets: named points, look, rules.
 */

import type { ComponentChildren } from 'preact';
import type {
  ActionButtonWidget, AlarmListWidget, ButtonShape, DayScheduleWidget, DialWidget, HeatingCurveWidget, MonthBarsWidget, PowerFlowWidget, SplitBarWidget, TankWidget, HeroAlternate, HeroSideValue, HeroValueWidget, HmiAction, ImageFit, ImageWidget,
  NavBarWidget, ShapeKind, ShapeWidget, SymbolWidget, TextAlign, TextWeight, TextWidget, WidgetDefinition,
  WidgetLook, WidgetRule, WidgetVariant,
} from '../../../widgets';
import { HmiAssetPicker } from '../../../../components/HmiAssetPicker/HmiAssetPicker';
import { check } from '../../../script/hmiScript';
import { ICON_NAMES, SYMBOLS, SYMBOL_NAMES } from '../../../panel/symbols';
import {
  BindingRow, CheckboxField, ColourField, Field, ListEditor, NumberField, Row, ScalarField, Section, SelectField,
  TextField, patcher, type WidgetEditorProps,
} from '../fields';

// ------------------------------------------------------------ script field

/** Script or condition, checked while typing. The message names the column
 *  so the author finds the typo without counting. */
export function ScriptField(props: {
  label: string;
  value: string | undefined;
  onChange(next: string | undefined): void;
  hint?: string;
  rows?: number;
}) {
  const err = props.value ? check(props.value) : null;
  return (
    <Field label={props.label} hint={err ? `Fehler bei Zeichen ${err.position + 1}: ${err.message}` : props.hint}>
      <textarea
        class={`hv-pf__input hv-pf__script${err ? ' hv-pf__input--error' : ''}`}
        rows={props.rows ?? 2}
        spellcheck={false}
        value={props.value ?? ''}
        onInput={(e) => {
          const raw = (e.currentTarget as HTMLTextAreaElement).value;
          props.onChange(raw === '' ? undefined : raw);
        }}
      />
    </Field>
  );
}

const SCRIPT_HINT = 'wert(name), schreibe(name, wert), begrenze(v, min, max), runde(v, schritt), wenn(b, a, c), gehe("bildschirm")';

// ------------------------------------------------------------ shared sections

interface NamedPoint { name: string; binding: string; }

export function NamedPointsSection({ widget, plantId, onChange }: {
  widget: WidgetDefinition; plantId: string; onChange(next: WidgetDefinition): void;
}) {
  const items: NamedPoint[] = Object.entries(widget.points ?? {}).map(([name, binding]) => ({ name, binding }));
  return (
    <Section title="Datenpunkte für Skripte und Regeln">
      <ListEditor<NamedPoint>
        label="Name zu Datenpunkt"
        hint="Den Namen verwenden Skripte, Regeln und Texte, etwa soll oder ist."
        items={items}
        create={() => ({ name: `p${items.length + 1}`, binding: '' })}
        addLabel="Datenpunkt hinzufügen"
        emptyHint="Noch keine benannten Datenpunkte."
        itemTitle={(i) => i.name || 'ohne Namen'}
        onChange={(next) => {
          const points: Record<string, string> = {};
          for (const n of next) if (n.name) points[n.name] = n.binding;
          onChange({ ...widget, points: next.length ? points : undefined });
        }}
        renderItem={(item, patch) => (
          <>
            <TextField label="Name" value={item.name} keepEmptyString
                       onChange={(v) => patch({ name: (v ?? '').replace(/[^A-Za-z0-9_]/g, '') })} />
            <BindingRow label="Datenpunkt" value={item.binding} plantId={plantId} onChange={(v) => patch({ binding: v })} />
          </>
        )}
      />
    </Section>
  );
}

const VARIANTS: { value: WidgetVariant; label: string }[] = [
  { value: 'card', label: 'Kachel' },
  { value: 'plain', label: 'Ohne Rahmen' },
  { value: 'outline', label: 'Umriss' },
  { value: 'accent', label: 'Akzentfarbe' },
];
const ALIGNS: { value: TextAlign; label: string }[] = [
  { value: 'start', label: 'Links' }, { value: 'center', label: 'Mitte' }, { value: 'end', label: 'Rechts' },
];

function LookFields({ look, onChange }: { look: WidgetLook; onChange(next: WidgetLook): void }) {
  const patch = (part: Partial<WidgetLook>) => onChange({ ...look, ...part });
  return (
    <>
      <Row>
        <SelectField label="Darstellung" value={look.variant ?? 'card'} options={VARIANTS} onChange={(v) => patch({ variant: v })} />
        <SelectField label="Ausrichtung" value={look.align ?? 'start'} options={ALIGNS} onChange={(v) => patch({ align: v })} />
      </Row>
      <Row>
        <NumberField label="Schriftgröße" value={look.fontScale} min={0.5} max={6} step={0.1} hint="1 = normal"
                     onChange={(v) => patch({ fontScale: v })} />
        <NumberField label="Ecken (px)" value={look.radius} min={0} max={400} integer onChange={(v) => patch({ radius: v })} />
        <NumberField label="Innenabstand" value={look.padding} min={0} max={200} integer onChange={(v) => patch({ padding: v })} />
      </Row>
      <ColourField label="Textfarbe" value={look.textColor} onChange={(v) => patch({ textColor: v })} />
      <ColourField label="Hintergrund" value={look.background} onChange={(v) => patch({ background: v })} />
      <ColourField label="Rahmenfarbe" value={look.borderColor} onChange={(v) => patch({ borderColor: v })} />
    </>
  );
}

export function LookSection({ widget, onChange }: { widget: WidgetDefinition; onChange(next: WidgetDefinition): void }) {
  const has = Boolean(widget.look);
  return (
    <Section title="Aussehen">
      <CheckboxField label="Eigenes Aussehen" checked={has}
                     onChange={(on) => onChange({ ...widget, look: on ? { variant: 'card' } : undefined })} />
      {has && <LookFields look={widget.look!} onChange={(look) => onChange({ ...widget, look })} />}
    </Section>
  );
}

export function RulesSection({ widget, onChange }: { widget: WidgetDefinition; onChange(next: WidgetDefinition): void }) {
  return (
    <Section title="Regeln">
      <ListEditor<WidgetRule>
        label="Wenn, dann"
        hint="Die erste zutreffende Regel gilt. Die Bedingung nutzt die benannten Datenpunkte."
        items={widget.rules ?? []}
        create={() => ({ when: '', look: { textColor: '#f85149' } })}
        addLabel="Regel hinzufügen"
        emptyHint="Keine Regeln."
        itemTitle={(r) => r.when || 'ohne Bedingung'}
        onChange={(next) => onChange({ ...widget, rules: next.length ? next : undefined })}
        renderItem={(rule, patch) => (
          <>
            <ScriptField label="Wenn" value={rule.when} hint="z. B. wert(ist) > wert(soll) + 2"
                         onChange={(v) => patch({ when: v ?? '' })} />
            <CheckboxField label="Dann ausblenden" checked={rule.hidden === true} onChange={(v) => patch({ hidden: v || undefined })} />
            <ColourField label="Dann Textfarbe" value={rule.look?.textColor}
                         onChange={(v) => patch({ look: { ...rule.look, textColor: v } })} />
            <ColourField label="Dann Hintergrund" value={rule.look?.background}
                         onChange={(v) => patch({ look: { ...rule.look, background: v } })} />
            <ColourField label="Dann Rahmen" value={rule.look?.borderColor}
                         onChange={(v) => patch({ look: { ...rule.look, borderColor: v } })} />
          </>
        )}
      />
    </Section>
  );
}

/** The three shared sections, in the order an author needs them. */
export function DesignSections(props: { widget: WidgetDefinition; plantId: string; onChange(next: WidgetDefinition): void }): ComponentChildren {
  return (
    <>
      <NamedPointsSection {...props} />
      <LookSection {...props} />
      <RulesSection {...props} />
    </>
  );
}

// ------------------------------------------------------------ action button

const SHAPES: { value: ButtonShape; label: string }[] = [
  { value: 'rounded', label: 'Abgerundet' }, { value: 'pill', label: 'Pille' },
  { value: 'circle', label: 'Kreis' }, { value: 'square', label: 'Eckig' },
];
const ACTION_TYPES: { value: HmiAction['type']; label: string }[] = [
  { value: 'script', label: 'Skript' }, { value: 'write', label: 'Wert setzen' }, { value: 'navigate', label: 'Bildschirm wechseln' },
];
const ICON_OPTIONS = [{ value: '', label: 'Kein Symbol' }, ...ICON_NAMES.map((n) => ({ value: n, label: n }))];

export function ActionButtonEditor({ widget, onChange }: WidgetEditorProps<ActionButtonWidget>) {
  const patch = patcher(widget, onChange);
  const pointNames = Object.keys(widget.points ?? {});
  return (
    <>
      <Section title="Knopf">
        <Row>
          <TextField label="Text" value={widget.text} placeholder="nur Symbol" onChange={(v) => patch({ text: v })} />
          <SelectField label="Symbol" value={widget.icon ?? ''} options={ICON_OPTIONS}
                       onChange={(v) => patch({ icon: v === '' ? undefined : v })} />
        </Row>
        <Row>
          <SelectField label="Form" value={widget.shape ?? 'rounded'} options={SHAPES} onChange={(v) => patch({ shape: v })} />
          <NumberField label="Halten wiederholt alle (ms)" value={widget.repeatMs} min={150} max={5000} integer
                       hint="Leer = ein Schritt je Druck" onChange={(v) => patch({ repeatMs: v })} />
        </Row>
        <TextField label="Nachfrage" value={widget.confirm} placeholder="ohne Nachfrage"
                   hint="Erster Druck fragt, zweiter führt aus." onChange={(v) => patch({ confirm: v })} />
      </Section>
      <Section title="Aktionen">
        <ListEditor<HmiAction>
          label="Der Reihe nach"
          items={widget.actions}
          required
          create={() => ({ type: 'script', script: '' })}
          addLabel="Aktion hinzufügen"
          itemTitle={(a) => ACTION_TYPES.find((t) => t.value === a.type)?.label ?? a.type}
          onChange={(next) => patch({ actions: next })}
          renderItem={(a, p) => (
            <>
              <SelectField label="Art" value={a.type} options={ACTION_TYPES} onChange={(v) => p({ type: v })} />
              {a.type === 'script' && (
                <ScriptField label="Skript" value={a.script} rows={3} hint={SCRIPT_HINT}
                             onChange={(v) => p({ script: v })} />
              )}
              {a.type === 'write' && (
                <>
                  <SelectField label="Datenpunkt" value={a.point ?? pointNames[0] ?? ''}
                               options={pointNames.length ? pointNames.map((n) => ({ value: n, label: n }))
                                 : [{ value: '', label: 'erst unten einen Datenpunkt benennen' }]}
                               onChange={(v) => p({ point: v })} />
                  <ScalarField label="Wert" value={a.value} onChange={(v) => p({ value: v })} />
                </>
              )}
              {a.type === 'navigate' && (
                <TextField label="Bildschirm-Id" value={a.screenId} onChange={(v) => p({ screenId: v })} />
              )}
            </>
          )}
        />
      </Section>
    </>
  );
}

// ------------------------------------------------------------ text

const WEIGHTS: { value: TextWeight; label: string }[] = [
  { value: 'light', label: 'Leicht' }, { value: 'regular', label: 'Normal' },
  { value: 'medium', label: 'Mittel' }, { value: 'bold', label: 'Fett' },
];

export function TextEditor({ widget, onChange }: WidgetEditorProps<TextWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <Section title="Text">
      <Field label="Inhalt" hint="{name} setzt den Wert eines benannten Datenpunkts ein.">
        <textarea class="hv-pf__input" rows={3} value={widget.content}
                  onInput={(e) => patch({ content: (e.currentTarget as HTMLTextAreaElement).value })} />
      </Field>
      <Row>
        <NumberField label="Größe (px)" value={widget.size} min={6} max={400} integer onChange={(v) => patch({ size: v })} />
        <SelectField label="Gewicht" value={widget.weight ?? 'regular'} options={WEIGHTS} onChange={(v) => patch({ weight: v })} />
      </Row>
      <CheckboxField label="Großbuchstaben" checked={widget.uppercase === true} onChange={(v) => patch({ uppercase: v || undefined })} />
    </Section>
  );
}

// ------------------------------------------------------------ hero value

export function HeroValueEditor({ widget, plantId, onChange }: WidgetEditorProps<HeroValueWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <>
      <Section title="Wert">
        <BindingRow label="Wert-Datenpunkt" value={widget.valuePoint} plantId={plantId} onChange={(v) => patch({ valuePoint: v })} />
        <TextField label="Beschriftung" value={widget.caption} placeholder="wie Beschriftung oben" onChange={(v) => patch({ caption: v })} />
        <Row>
          <TextField label="Einheit" value={widget.unit} placeholder="wie Datenpunkt" hint="° wird hochgestellt" onChange={(v) => patch({ unit: v })} />
          <NumberField label="Nachkommastellen" value={widget.decimals} min={0} max={4} integer onChange={(v) => patch({ decimals: v })} />
        </Row>
      </Section>
      <Section title="Nebenwerte">
        <ListEditor<HeroSideValue>
          label="Klein darunter"
          items={widget.sideValues ?? []}
          create={() => ({ label: 'Soll', point: '' })}
          addLabel="Nebenwert hinzufügen"
          itemTitle={(s) => s.label}
          onChange={(next) => patch({ sideValues: next.length ? next : undefined })}
          renderItem={(s, p) => (
            <>
              <TextField label="Beschriftung" value={s.label} keepEmptyString onChange={(v) => p({ label: v ?? '' })} />
              <BindingRow label="Datenpunkt" value={s.point} plantId={plantId} onChange={(v) => p({ point: v })} />
              <Row>
                <TextField label="Einheit" value={s.unit} onChange={(v) => p({ unit: v })} />
                <NumberField label="Nachkommastellen" value={s.decimals} min={0} max={4} integer onChange={(v) => p({ decimals: v })} />
              </Row>
            </>
          )}
        />
      </Section>
      <Section title="Abwechselnd zeigen">
        <ListEditor<HeroAlternate>
          label="Weitere Werte, nacheinander"
          items={widget.alternates ?? []}
          create={() => ({ valuePoint: '', caption: '' })}
          addLabel="Wert hinzufügen"
          itemTitle={(a) => a.caption || 'Wert'}
          onChange={(next) => patch({ alternates: next.length ? next : undefined })}
          renderItem={(a, p) => (
            <>
              <TextField label="Beschriftung" value={a.caption} onChange={(v) => p({ caption: v })} />
              <BindingRow label="Wert-Datenpunkt" value={a.valuePoint} plantId={plantId} onChange={(v) => p({ valuePoint: v })} />
              <Row>
                <TextField label="Einheit" value={a.unit} onChange={(v) => p({ unit: v })} />
                <NumberField label="Nachkommastellen" value={a.decimals} min={0} max={4} integer onChange={(v) => p({ decimals: v })} />
              </Row>
            </>
          )}
        />
        {widget.alternates && widget.alternates.length > 0 && (
          <Row>
            <TextField label="Beschriftung des ersten Werts beim Wechseln" value={widget.cycleCaption}
                       placeholder={widget.caption} onChange={(v) => patch({ cycleCaption: v })} />
            <NumberField label="Sekunden je Wert" value={widget.cycleSeconds} min={0} max={120} integer
                         hint="0 heißt nur per Antippen" onChange={(v) => patch({ cycleSeconds: v })} />
          </Row>
        )}
      </Section>
    </>
  );
}

// ------------------------------------------------------------ image

const FITS: { value: ImageFit; label: string }[] = [
  { value: 'contain', label: 'Einpassen' }, { value: 'cover', label: 'Ausfüllen' }, { value: 'fill', label: 'Strecken' },
];

export function ImageEditor({ widget, onChange }: WidgetEditorProps<ImageWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <Section title="Bild">
      <HmiAssetPicker value={widget.assetRef || null} onChange={(next) => patch({ assetRef: next ?? '' })} />
      <Row>
        <SelectField label="Einpassung" value={widget.fit ?? 'contain'} options={FITS} onChange={(v) => patch({ fit: v })} />
        <NumberField label="Deckkraft" value={widget.opacity} min={0} max={1} step={0.05} onChange={(v) => patch({ opacity: v })} />
      </Row>
    </Section>
  );
}

// ------------------------------------------------------------ shape

const SHAPE_KINDS: { value: ShapeKind; label: string }[] = [
  { value: 'pipe', label: 'Rohr' }, { value: 'line', label: 'Linie' },
  { value: 'rect', label: 'Rechteck' }, { value: 'ellipse', label: 'Kreis / Ellipse' },
];

export function ShapeEditor({ widget, onChange }: WidgetEditorProps<ShapeWidget>) {
  const patch = patcher(widget, onChange);
  const linear = widget.shape === 'pipe' || widget.shape === 'line';
  return (
    <Section title="Form">
      <Row>
        <SelectField label="Form" value={widget.shape} options={SHAPE_KINDS} onChange={(v) => patch({ shape: v })} />
        {linear && (
          <SelectField label="Richtung" value={widget.orientation ?? 'horizontal'}
                       options={[{ value: 'horizontal', label: 'Waagrecht' }, { value: 'vertical', label: 'Senkrecht' }]}
                       onChange={(v) => patch({ orientation: v as 'horizontal' | 'vertical' })} />
        )}
      </Row>
      <NumberField label="Strichstärke" value={widget.strokeWidth} min={0} max={60} onChange={(v) => patch({ strokeWidth: v })} />
      <ColourField label="Linienfarbe" value={widget.stroke} hint="leer: Akzentfarbe des Themes" onChange={(v) => patch({ stroke: v })} />
      {!linear && <ColourField label="Füllung" value={widget.fill} onChange={(v) => patch({ fill: v })} />}
      {widget.shape === 'pipe' && (
        <>
          <ScriptField label="Fließt, wenn" value={widget.flowWhen} hint="z. B. wert(pumpe) == 1"
                       onChange={(v) => patch({ flowWhen: v })} />
          <CheckboxField label="Gegen die Zeichenrichtung" checked={widget.flowReverse === true}
                         onChange={(v) => patch({ flowReverse: v || undefined })} />
        </>
      )}
    </Section>
  );
}

// ------------------------------------------------------------ symbol

const SYMBOL_OPTIONS = SYMBOL_NAMES.map((n) => ({ value: n, label: SYMBOLS[n]!.label }));

export function SymbolEditor({ widget, onChange }: WidgetEditorProps<SymbolWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <Section title="Symbol">
      <SelectField label="Symbol" value={widget.symbol} options={SYMBOL_OPTIONS} onChange={(v) => patch({ symbol: v })} />
      <ScriptField label="Läuft, wenn" value={widget.runningWhen} hint="z. B. wert(verdichter) > 0" onChange={(v) => patch({ runningWhen: v })} />
      <ScriptField label="Störung, wenn" value={widget.alarmWhen} hint="z. B. wert(alarm) == 0" onChange={(v) => patch({ alarmWhen: v })} />
      <ColourField label="Farbe im Lauf" value={widget.color} hint="leer: Akzentfarbe" onChange={(v) => patch({ color: v })} />
    </Section>
  );
}

// ------------------------------------------------------------ nav bar

export function NavBarEditor({ widget, onChange }: WidgetEditorProps<NavBarWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <Section title="Navigation">
      <TextField label="Bildschirme (Ids, mit Komma)" value={widget.screenIds?.join(', ')} placeholder="alle"
                 onChange={(v) => patch({ screenIds: v ? v.split(',').map((x) => x.trim()).filter(Boolean) : undefined })} />
      <CheckboxField label="Symbole zeigen" checked={widget.showIcons !== false} onChange={(v) => patch({ showIcons: v })} />
      <CheckboxField label="Beschriftung zeigen" checked={widget.showLabels !== false} onChange={(v) => patch({ showLabels: v })} />
    </Section>
  );
}

// ------------------------------------------------------------ alarm list

/** Each named point is one alarm; here it gets its text. The points
 *  themselves come from the "Datenpunkte" section below. */
export function AlarmListEditor({ widget, onChange }: WidgetEditorProps<AlarmListWidget>) {
  const patch = patcher(widget, onChange);
  const names = Object.keys(widget.points ?? {});
  const labelOf = (n: string) => widget.entries.find((e) => e.point === n)?.label;
  const setLabel = (n: string, label: string) => patch({
    entries: names.map((x) => ({ point: x, label: x === n ? label : labelOf(x) ?? x })),
  });
  return (
    <>
      <Section title="Meldungsliste">
        <TextField label="Überschrift" value={widget.title} placeholder="ohne" onChange={(v) => patch({ title: v || undefined })} />
        <TextField label="Ohne Meldung" value={widget.emptyText} placeholder="Keine Meldungen"
                   onChange={(v) => patch({ emptyText: v || undefined })} />
      </Section>
      <Section title="Meldungstexte" hint="Je Alarm ein Datenpunkt (unten). Wert 1 heißt: steht an.">
        {names.map((n) => (
          <TextField key={n} label={n} value={labelOf(n)} placeholder={n} onChange={(v) => setLabel(n, v ?? '')} />
        ))}
      </Section>
    </>
  );
}

// ------------------------------------------------------------ dial

export function DialEditor({ widget, plantId, onChange }: WidgetEditorProps<DialWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <Section title="Bogenskala">
      <BindingRow label="Wert-Datenpunkt" value={widget.valuePoint} plantId={plantId} onChange={(v) => patch({ valuePoint: v })} />
      <BindingRow label="Merker (Istwert)" value={widget.markerPoint ?? ''} plantId={plantId} onChange={(v) => patch({ markerPoint: v || undefined })} />
      <Row>
        <TextField label="Beschriftung" value={widget.caption} onChange={(v) => patch({ caption: v })} />
        <TextField label="Merker heißt" value={widget.markerLabel} placeholder="Ist" onChange={(v) => patch({ markerLabel: v })} />
      </Row>
      <Row>
        <NumberField label="Von" value={widget.min} onChange={(v) => patch({ min: v ?? 0 })} />
        <NumberField label="Bis" value={widget.max} onChange={(v) => patch({ max: v ?? 100 })} />
      </Row>
      <Row>
        <TextField label="Einheit" value={widget.unit} placeholder="wie Datenpunkt" onChange={(v) => patch({ unit: v })} />
        <NumberField label="Nachkommastellen" value={widget.decimals} min={0} max={3} integer onChange={(v) => patch({ decimals: v })} />
      </Row>
    </Section>
  );
}

// ------------------------------------------------------------ day schedule

type DayField = 'mondayPoint' | 'tuesdayPoint' | 'wednesdayPoint' | 'thursdayPoint' | 'fridayPoint' | 'saturdayPoint' | 'sundayPoint';
const WEEKDAYS: [DayField, string][] = [
  ['mondayPoint', 'Montag'], ['tuesdayPoint', 'Dienstag'], ['wednesdayPoint', 'Mittwoch'], ['thursdayPoint', 'Donnerstag'],
  ['fridayPoint', 'Freitag'], ['saturdayPoint', 'Samstag'], ['sundayPoint', 'Sonntag'],
];

export function DayScheduleEditor({ widget, plantId, onChange }: WidgetEditorProps<DayScheduleWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <>
      <Section title="Zeitprogramm" hint="Je Wochentag ein Datenpunkt mit Text wie 06:00-22:00, --, --.">
        <TextField label="Überschrift" value={widget.title} placeholder="Zeitprogramm heute" onChange={(v) => patch({ title: v })} />
        {WEEKDAYS.map(([field, label]) => (
          <BindingRow key={field} label={label} value={widget[field]} plantId={plantId}
                      onChange={(v) => patch({ [field]: v } as Partial<DayScheduleWidget>)} />
        ))}
      </Section>
      <Section title="Nur im Automatikbetrieb">
        <BindingRow label="Betriebsart" value={widget.modePoint ?? ''} plantId={plantId} onChange={(v) => patch({ modePoint: v || undefined })} />
        <NumberField label="Wert für Automatik" value={widget.autoValue} integer onChange={(v) => patch({ autoValue: v })} />
      </Section>
    </>
  );
}

// ------------------------------------------------------------ heating curve

type CurveField = 'slopePoint' | 'shiftPoint' | 'roomSetpointPoint' | 'outsidePoint' | 'summerPoint' | 'flowSetpointPoint' | 'flowPoint';

export function HeatingCurveEditor({ widget, plantId, onChange }: WidgetEditorProps<HeatingCurveWidget>) {
  const patch = patcher(widget, onChange);
  const row = (label: string, field: CurveField) => (
    <BindingRow label={label} value={widget[field] ?? ''} plantId={plantId}
                onChange={(v) => patch({ [field]: field === 'slopePoint' ? v : (v || undefined) } as Partial<HeatingCurveWidget>)} />
  );
  return (
    <Section title="Heizkennlinie">
      <TextField label="Überschrift" value={widget.title} placeholder="Heizkennlinie" onChange={(v) => patch({ title: v })} />
      {row('Steilheit', 'slopePoint')}
      {row('Parallelverschiebung', 'shiftPoint')}
      {row('Raumsollwert', 'roomSetpointPoint')}
      {row('Außentemperatur', 'outsidePoint')}
      {row('Sommergrenze', 'summerPoint')}
      {row('Vorlauf soll', 'flowSetpointPoint')}
      {row('Vorlauf ist', 'flowPoint')}
    </Section>
  );
}

// ------------------------------------------------------------ tank

export function TankEditor({ widget, plantId, onChange }: WidgetEditorProps<TankWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <Section title="Speicher">
      <TextField label="Beschriftung" value={widget.caption} onChange={(v) => patch({ caption: v })} />
      <BindingRow label="Temperatur oben" value={widget.topPoint} plantId={plantId} onChange={(v) => patch({ topPoint: v })} />
      <BindingRow label="Temperatur unten" value={widget.bottomPoint ?? ''} plantId={plantId} onChange={(v) => patch({ bottomPoint: v || undefined })} />
      <BindingRow label="Sollwert" value={widget.setpointPoint ?? ''} plantId={plantId} onChange={(v) => patch({ setpointPoint: v || undefined })} />
      <ScriptField label="Lädt, wenn" value={widget.chargingWhen} hint="Namen aus den Datenpunkten unten, z. B. wert(push) == 1" onChange={(v) => patch({ chargingWhen: v })} />
    </Section>
  );
}

// ------------------------------------------------------------ energy

export function PowerFlowEditor({ widget, plantId, onChange }: WidgetEditorProps<PowerFlowWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <Section title="Energiefluss" hint="Fehlt die Arbeitszahl, rechnet der Baustein Wärme durch Strom.">
      <BindingRow label="Strom (kW)" value={widget.elPoint} plantId={plantId} onChange={(v) => patch({ elPoint: v })} />
      <BindingRow label="Wärme (kW)" value={widget.heatPoint} plantId={plantId} onChange={(v) => patch({ heatPoint: v })} />
      <BindingRow label="Arbeitszahl" value={widget.copPoint ?? ''} plantId={plantId} onChange={(v) => patch({ copPoint: v || undefined })} />
      <Row>
        <TextField label="Quelle Strom" value={widget.elSource} placeholder="Stromzähler" onChange={(v) => patch({ elSource: v })} />
        <TextField label="Quelle Wärme" value={widget.heatSource} placeholder="Wärmemengenzähler" onChange={(v) => patch({ heatSource: v })} />
      </Row>
    </Section>
  );
}

export function MonthBarsEditor({ widget, plantId, onChange }: WidgetEditorProps<MonthBarsWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <Section title="Monatsbalken" hint="Der Datenpunkt liefert eine Liste von zwölf Zahlen, Jänner zuerst.">
      <TextField label="Überschrift" value={widget.title} onChange={(v) => patch({ title: v })} />
      <BindingRow label="Monatswerte" value={widget.valuesPoint ?? ''} plantId={plantId} onChange={(v) => patch({ valuesPoint: v })} />
      <BindingRow label="Bezugslinie" value={widget.referencePoint ?? ''} plantId={plantId} onChange={(v) => patch({ referencePoint: v || undefined })} />
      <Row>
        <TextField label="Bezugslinie heißt" value={widget.referenceLabel} placeholder="JAZ" onChange={(v) => patch({ referenceLabel: v })} />
        <NumberField label="Höchstwert" value={widget.max} onChange={(v) => patch({ max: v })} />
      </Row>
    </Section>
  );
}

export function SplitBarEditor({ widget, plantId, onChange }: WidgetEditorProps<SplitBarWidget>) {
  const patch = patcher(widget, onChange);
  return (
    <Section title="Verhältnisbalken">
      <TextField label="Überschrift" value={widget.title} placeholder="Dieses Jahr" onChange={(v) => patch({ title: v })} />
      <BindingRow label="Erste Menge" value={widget.aPoint ?? ''} plantId={plantId} onChange={(v) => patch({ aPoint: v })} />
      <BindingRow label="Zweite Menge" value={widget.bPoint ?? ''} plantId={plantId} onChange={(v) => patch({ bPoint: v })} />
      <Row>
        <TextField label="Erste heißt" value={widget.aLabel} placeholder="Strom" onChange={(v) => patch({ aLabel: v })} />
        <TextField label="Zweite heißt" value={widget.bLabel} placeholder="Wärme" onChange={(v) => patch({ bLabel: v })} />
        <TextField label="Einheit" value={widget.unit} placeholder="kWh" onChange={(v) => patch({ unit: v })} />
      </Row>
    </Section>
  );
}

